/**
 * The second line of an authorisation control, and never the first.
 *
 * Platform design §3: nothing client-side decides authorisation. The control is
 * the API refusing the call; hiding a menu entry is a courtesy for the caller,
 * not a barrier. This guard exists because hiding is where that rule usually
 * dies -- a route whose only protection is an absent nav item is reachable by
 * typing its URL, and then the shell renders a page shaped like data the caller
 * cannot have, with an empty body and a stack of failed requests behind it.
 *
 * Every branch that is not an affirmative "the server said this caller holds
 * this capability" refuses. Loading refuses, a failed query refuses, and a
 * capability list that does not contain the requirement refuses.
 */

import type { ReactNode } from 'react';

import { t } from '../../i18n';
import { useMe } from '../session/session';
import { Forbidden, SessionUnavailable } from './Forbidden';

export function RequireCapability(props: { capability: string; children: ReactNode }): ReactNode {
  const me = useMe();

  if (me.isPending) {
    return (
      <p role="status" className="text-muted-foreground p-8 text-sm">
        {t('shell.loading')}
      </p>
    );
  }

  if (me.data === undefined) {
    return <SessionUnavailable onRetry={() => void me.refetch()} />;
  }

  if (!me.data.capabilities.includes(props.capability)) {
    return <Forbidden />;
  }

  return props.children;
}
